import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Trash2, Flame, AlertTriangle, CheckCircle2, Database, Clock, Zap, Lock, LogOut } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOKEN_KEY = "aioo_admin_token";

const adminAxios = (token) => axios.create({
  baseURL: API,
  headers: { "X-Admin-Token": token || "" },
});

const formatAge = (h) => {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
};

const Btn = ({ onClick, busy, icon: Icon, label, variant = "neutral", testId }) => {
  const styles = {
    neutral: "bg-white/[0.05] hover:bg-white/[0.10] border-white/[0.10] text-slate-200",
    danger: "bg-red-500/10 hover:bg-red-500/20 border-red-500/30 text-red-300",
    warm: "bg-amber-500/15 hover:bg-amber-500/25 border-amber-500/30 text-amber-300",
  }[variant];
  return (
    <button onClick={onClick} disabled={busy} data-testid={testId}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all text-xs font-medium disabled:opacity-50 ${styles}`}>
      <Icon className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
      {busy ? "Working..." : label}
    </button>
  );
};

const StatTile = ({ label, value, sublabel, color = "text-slate-200" }) => (
  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1.5">{label}</div>
    <div className={`text-2xl font-black tabular-nums ${color}`}>{value}</div>
    {sublabel && <div className="text-[10px] text-slate-400 mt-1">{sublabel}</div>}
  </div>
);

export const AdminDashboard = () => {
  // sessionStorage so the admin token is cleared on tab close and isn't persisted
  // across browser restarts. Still readable by JS in this tab — there is no httpOnly
  // option for client-side fetches without a server-issued cookie session, which is
  // out of scope for this single-admin internal dashboard.
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [authChecked, setAuthChecked] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});

  const api = useCallback(() => adminAxios(token), [token]);

  // Verify token on mount / change
  useEffect(() => {
    if (!token) {
      setAuthChecked(true);
      setAuthed(false);
      return;
    }
    setLoading(true);
    api().get("/admin/cache-health")
      .then(r => {
        setHealth(r.data);
        setAuthed(true);
      })
      .catch(err => {
        if (err.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
          setAuthed(false);
          toast.error("Invalid admin token");
        }
      })
      .finally(() => {
        setAuthChecked(true);
        setLoading(false);
      });
  }, [token, api]);

  const submitToken = (e) => {
    e?.preventDefault();
    if (!tokenInput.trim()) return;
    sessionStorage.setItem(TOKEN_KEY, tokenInput.trim());
    localStorage.removeItem(TOKEN_KEY);  // migrate any legacy localStorage tokens off
    setToken(tokenInput.trim());
    setTokenInput("");
  };

  const signOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setHealth(null);
    toast("Signed out");
  };

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api().get("/admin/cache-health");
      setHealth(r.data);
    } catch {
      toast.error("Failed to load cache health");
    }
    setLoading(false);
  }, [api]);

  const setBusyKey = (k, v) => setBusy(b => ({ ...b, [k]: v }));

  const onWarm = async () => {
    setBusyKey("warm", true);
    try {
      const r = await api().post("/admin/warm-industry-futures");
      toast.success(`Warming started for ${r.data.industries?.length || 0} industries + ${r.data.country_pairs}`, {
        description: "Runs in background ~3-4 min. Refresh later to see updated cache.",
      });
    } catch {
      toast.error("Warm trigger failed");
    }
    setBusyKey("warm", false);
  };

  const onPurge = async () => {
    setBusyKey("purge", true);
    try {
      const r = await api().get("/admin/cache-health?purge=true");
      toast.success(`Purged ${r.data.purged || 0} poisoned entries`);
      setHealth(r.data);
    } catch {
      toast.error("Purge failed");
    }
    setBusyKey("purge", false);
  };

  const onInvalidate = async () => {
    setBusyKey("invalidate", true);
    try {
      await api().post("/admin/invalidate-bootstrap");
      toast.success("Bootstrap cache invalidated", {
        description: "Next /api/bootstrap call will re-read from disk",
      });
    } catch {
      toast.error("Invalidate failed");
    }
    setBusyKey("invalidate", false);
  };

  // Auth gate
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#060a12] flex items-center justify-center">
        <RefreshCw className="w-5 h-5 text-slate-500 animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#060a12] text-slate-200 flex items-center justify-center p-6" data-testid="admin-login">
        <form onSubmit={submitToken} className="w-full max-w-sm bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-400" />
            <h1 className="text-base font-bold">Admin Access</h1>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            Enter your admin token to manage caches and warmers. Token is stored in your browser only.
          </p>
          <input
            type="password"
            value={tokenInput}
            onChange={e => setTokenInput(e.target.value)}
            placeholder="Admin token"
            data-testid="admin-token-input"
            className="w-full px-3 py-2.5 bg-black/40 border border-white/[0.08] rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/40"
            autoFocus
          />
          <div className="flex gap-2">
            <button type="submit" disabled={!tokenInput.trim()} data-testid="admin-token-submit"
              className="flex-1 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 disabled:opacity-30 border border-amber-500/30 rounded-lg text-xs font-bold text-amber-200 transition-all">
              Unlock
            </button>
            <button type="button" onClick={() => { window.location.hash = ""; window.location.reload(); }}
              className="px-4 py-2 text-xs text-slate-400 hover:text-slate-300">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  const industryFutures = (health?.healthy || []).filter(e => e.key.startsWith("industry_futures_"));
  const compareHistorical = (health?.healthy || []).filter(e => e.key.startsWith("compare_historical_"));
  const industryInsights = (health?.healthy || []).filter(e => e.key.startsWith("industry_insights_"));
  const deepQueries = (health?.healthy || []).filter(e => e.key.startsWith("deep_query_"));
  const otherCached = (health?.healthy || []).filter(e =>
    !e.key.startsWith("industry_futures_") &&
    !e.key.startsWith("compare_historical_") &&
    !e.key.startsWith("industry_insights_") &&
    !e.key.startsWith("deep_query_")
  );

  return (
    <div className="min-h-screen bg-[#060a12] text-slate-200 p-6 md:p-10" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-black tracking-tight">Admin · Cache Health</h1>
            <p className="text-xs text-slate-500 mt-1">Monitor warmers, invalidate caches, purge poisoned entries</p>
          </div>
          <div className="flex items-center gap-2">
            <Btn onClick={fetchHealth} busy={loading} icon={RefreshCw} label="Refresh" testId="admin-refresh" />
            <Btn onClick={signOut} busy={false} icon={LogOut} label="Sign Out" testId="admin-signout" />
          </div>
        </div>

        {/* Top stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile
            label="Healthy"
            value={health?.healthy_count ?? "—"}
            sublabel="cache entries"
            color="text-emerald-400"
          />
          <StatTile
            label="Poisoned"
            value={health?.poisoned_count ?? "—"}
            sublabel={health?.poisoned_count ? "needs purge" : "none"}
            color={health?.poisoned_count ? "text-red-400" : "text-slate-200"}
          />
          <StatTile
            label="Industry Futures"
            value={industryFutures.length}
            sublabel={industryFutures[0] ? `last warmed ${formatAge(industryFutures[0].age_hours)} ago` : "not warmed"}
            color={industryFutures.length >= 5 ? "text-emerald-400" : "text-amber-400"}
          />
          <StatTile
            label="Compare Time Machine"
            value={compareHistorical.length}
            sublabel={compareHistorical.length >= 15 ? "fully warmed" : `${15 - compareHistorical.length} pairs missing`}
            color={compareHistorical.length >= 15 ? "text-emerald-400" : "text-amber-400"}
          />
        </div>

        {/* Action bar */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-3">Actions</div>
          <div className="flex flex-wrap gap-2">
            <Btn onClick={onWarm} busy={busy.warm} icon={Flame} label="Warm Caches" variant="warm" testId="admin-warm" />
            <Btn onClick={onInvalidate} busy={busy.invalidate} icon={Zap} label="Invalidate Bootstrap" testId="admin-invalidate" />
            <Btn
              onClick={onPurge}
              busy={busy.purge}
              icon={Trash2}
              label={`Purge Poisoned${health?.poisoned_count ? ` (${health.poisoned_count})` : ""}`}
              variant={health?.poisoned_count ? "danger" : "neutral"}
              testId="admin-purge"
            />
          </div>
          <p className="text-[10px] text-slate-500 mt-3">
            Warm Caches: regenerates Industry Futures + Country Compare Time Machine (~3-4 min in background) ·
            Invalidate Bootstrap: forces /api/bootstrap to re-read from disk ·
            Purge Poisoned: deletes any cache entries with status=no_data or total=0
          </p>
        </div>

        {/* Poisoned warning */}
        {health?.poisoned?.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/25 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-xs font-bold text-red-300 uppercase tracking-wider">
                {health.poisoned.length} Poisoned {health.poisoned.length === 1 ? "Entry" : "Entries"}
              </span>
            </div>
            <ul className="space-y-1 text-xs text-red-200/80 font-mono">
              {health.poisoned.slice(0, 10).map(p => (
                <li key={p.key} className="flex items-center justify-between">
                  <span>{p.key}</span>
                  <span className="text-red-400/60 italic">{p.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Cached entries breakdown */}
        <CacheGroup title="Industry Futures" icon={Database} entries={industryFutures} expectedCount={5} />
        <CacheGroup title="Industry Insights" icon={Database} entries={industryInsights} expectedCount={20} />
        <CacheGroup title="Compare Time Machine" icon={Clock} entries={compareHistorical} expectedCount={15} />
        <CacheGroup title="Deep-Query (Ask the Data)" icon={Database} entries={deepQueries} />
        <CacheGroup title="Other Bootstrap-style" icon={Database} entries={otherCached} />

        {/* Quick links */}
        <div className="text-center pt-4 border-t border-white/[0.04]">
          <button onClick={() => { window.location.hash = ""; window.location.reload(); }}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
            data-testid="admin-back">
            ← Back to Observatory
          </button>
        </div>
      </div>
    </div>
  );
};

const CacheGroup = ({ title, icon: Icon, entries, expectedCount }) => {
  const [open, setOpen] = useState(false);
  if (entries.length === 0 && !expectedCount) return null;
  const isComplete = expectedCount ? entries.length >= expectedCount : entries.length > 0;

  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
        data-testid={`cache-group-${title.toLowerCase().replace(/\s/g, "-")}`}>
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs font-semibold">{title}</span>
          <span className="text-[10px] text-slate-500">
            {entries.length}{expectedCount ? `/${expectedCount}` : ""}
          </span>
          {isComplete && entries.length > 0 && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
        </div>
        <span className="text-[10px] text-slate-500">{open ? "▾" : "▸"}</span>
      </button>
      {open && entries.length > 0 && (
        <ul className="border-t border-white/[0.04] divide-y divide-white/[0.04]">
          {entries.slice(0, 30).map(e => (
            <li key={e.key} className="px-4 py-2 text-[11px] flex items-center justify-between">
              <span className="font-mono text-slate-400 truncate">{e.key}</span>
              <span className="text-slate-500 ml-2">{formatAge(e.age_hours)}</span>
            </li>
          ))}
          {entries.length > 30 && (
            <li className="px-4 py-2 text-[10px] text-slate-500 italic">+ {entries.length - 30} more</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default AdminDashboard;
